import type { UseFormReturn } from 'react-hook-form';
import type { TFunction } from 'i18next';
import { FormControl, FormField, FormItem, FormMessage } from '@r/components/ui/form.tsx';
import {
  isSlideFieldRequired,
  type ItemDefinition,
  type SlideFormValues
} from '@admin/pages/personalization/content-block-slides.ts';
import {
  FIELD_HINT_IN_TOOLTIP,
  getSlideFieldDisplayLabel,
  isMobileBackgroundImageDefinition
} from '@admin/components/personalization/content-block-slide-form-layout.ts';
import { ContentBlockSlideFieldLabel } from '@admin/components/personalization/content-block-slide-form-field-label.tsx';
import { ContentBlockSlideFieldControl } from '@admin/components/personalization/content-block-slide-form-field-control.tsx';
import {
  FILTER_PRESETS,
  resolveFilterPresetValue,
  type TextColorPreset
} from '@admin/components/personalization/content-block-slide-form-field-utils.ts';

export interface ContentBlockSlideDefinitionFieldProps {
  itemDefinition: ItemDefinition;
  form: UseFormReturn<SlideFormValues>;
  t: TFunction;
  textColorPresets: TextColorPreset[];
  progresses: Record<string, number> | undefined;
  mediaFieldDisabled: boolean;
  onMediaUpload: (
    itemKey: string,
    files: File[],
    onChange: (value: string) => void
  ) => void | Promise<void>;
}

/** Un champ formulaire lié à une définition widget (label + contrôle + validation). */
export const ContentBlockSlideDefinitionField = ({
  itemDefinition,
  form,
  t,
  textColorPresets,
  progresses,
  mediaFieldDisabled,
  onMediaUpload
}: ContentBlockSlideDefinitionFieldProps) => (
  <FormField
    control={form.control}
    name={itemDefinition.itemKey}
    render={({ field }) => {
      const filterCurrentValue =
        itemDefinition.itemKey === 'filter_image' ? resolveFilterPresetValue(field.value) : null;
      const filterSelectedPreset = filterCurrentValue
        ? FILTER_PRESETS.find((preset) => preset.value === filterCurrentValue)
        : null;

      const hintInTooltip =
        FIELD_HINT_IN_TOOLTIP.has(itemDefinition.itemKey) ||
        isMobileBackgroundImageDefinition(itemDefinition);
      const hint = itemDefinition.itemDescription?.trim();
      const required = isSlideFieldRequired(itemDefinition);
      const displayLabel = getSlideFieldDisplayLabel(itemDefinition, t);

      return (
        <FormItem>
          {itemDefinition.itemKey === 'filter_image' ? (
            <div className="flex flex-wrap gap-2 justify-between items-center">
              <ContentBlockSlideFieldLabel
                label={displayLabel}
                hint={hint}
                showHintInTooltip={hintInTooltip}
                required={required}
              />
              <span className="text-sm font-semibold text-primary">
                {filterSelectedPreset ? t(filterSelectedPreset.labelKey) : filterCurrentValue}
              </span>
            </div>
          ) : (
            <ContentBlockSlideFieldLabel
              label={displayLabel}
              hint={hint}
              showHintInTooltip={hintInTooltip}
              required={required}
            />
          )}
          <FormControl>
            <ContentBlockSlideFieldControl
              itemDefinition={itemDefinition}
              value={field.value}
              onChange={field.onChange}
              t={t}
              textColorPresets={textColorPresets}
              progresses={progresses}
              mediaFieldDisabled={mediaFieldDisabled}
              onMediaUpload={onMediaUpload}
            />
          </FormControl>
          {hint && !hintInTooltip ? (
            <p className="text-xs leading-relaxed text-muted-foreground">{hint}</p>
          ) : null}
          <FormMessage />
        </FormItem>
      );
    }}
  />
);
